import { Doc } from '@dadohr/shared/domainTypes/document';
import { CalendlyIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/calendly';
import { Button, Dialog, DialogContent, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { InfoBox } from '../../../../../../components/InfoBox';
import { LinkExternal } from '../../../../../../components/LinkExternal';
import { Loader } from '../../../../../../components/Loader';
import ENVS from '../../../../../../env.json';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { OAuthSetupDialog } from '../oauth';
import {
  removeCalendlyConfig,
  useCalendlyConfig,
  useCalendlyCurrentUser
} from './service';

const NAME = 'Calendly';
const OAUTH = ENVS.calendly.oauth;

export const CALENDLY_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,
  cloudFn: 'calendly_exchangeCodeAndStoreCredentials'
};

const InitialSetupDialog = ({
  orgId,
  open,
  onClose
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <OAuthSetupDialog
      orgId={orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthUrl={'https://auth.calendly.com/oauth/authorize'}
      clientId={OAUTH.clientId}
      redirectUri={OAUTH.redirectUri}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.calendly.setupInfo.p1')}
      </Alert>
      <Typography component="p" variant="body1">
        {t('org.integrations.calendly.setupInfo.p2')}
      </Typography>
      <InfoBox>{t('org.integrations.calendly.setupInfo.p3')}</InfoBox>
    </OAuthSetupDialog>
  );
};

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<CalendlyIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [user, loading] = useCalendlyCurrentUser(config.data.orgId);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <DialogContent>
        <Alert severity="info" icon={false}>
          {t('org.integrations.calendly.setupInfo.p1')}
        </Alert>
        {loading && !user && <Loader height={300} />}
        {user && (
          <>
            <IntegrationData>
              <strong>{t('org.integrations.common.email')}</strong>
              <div>{user.email}</div>
              <strong>
                {t('org.integrations.calendly.existingConfig.schedulingUrl')}
              </strong>
              <div>
                <LinkExternal href={user.scheduling_url} />
              </div>
              <strong>{t('org.integrations.common.accessToken')}</strong>
              <div>{masked(config.data.accessToken)}</div>
              <strong>{t('org.integrations.common.refreshToken')}</strong>
              <div>{masked(config.data.refreshToken)}</div>
            </IntegrationData>
          </>
        )}
      </DialogContent>
      <DialogActionsWithSlots
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            disabled={loading}
            onClick={() => removeCalendlyConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
      />
    </Dialog>
  );
};

interface SetupDialogProps
  extends IntegrationSetupDialogWithConfigProperties<CalendlyIntegrationConfig> {
  orgId: string;
}
export const SetupDialog = ({
  config,
  onClose,
  orgId,
  onSuccessfulIntegration, // can be ignored since this integration is not an import source
  open
}: SetupDialogProps) => {
  const setup = !!(config?.data.accessToken && config?.data.refreshToken);
  return (
    <>
      {!setup && (
        <InitialSetupDialog orgId={orgId} open={open} onClose={onClose} />
      )}
      {setup && config && (
        <ExistingSetupDialog config={config} open={open} onClose={onClose} />
      )}
    </>
  );
};

export const CalendlySettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useCalendlyConfig(org.id, userId);
  const cfg = config || null;

  return (
    <IntegrationConfigurationButton
      functionalities={functionalities}
      name={NAME}
      logoUrl="/images/integrations-logos/calendly.svg"
      active={!!cfg?.data.active}
      interactive={interactive}
      renderSetupDialog={({
        open,
        onClose,
        onFailure,
        onSuccessfulIntegration
      }: IntegrationSetupDialogProperties) => (
        <SetupDialog
          orgId={org.id}
          config={cfg}
          open={open}
          onClose={onClose}
          onFailure={onFailure}
          onSuccessfulIntegration={onSuccessfulIntegration}
        />
      )}
    />
  );
};
